import React, { useRef, useState } from "react";
import { ImagePlus, Mail, Plus, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const inputCls = "w-full bg-transparent border border-white/15 px-4 py-2.5 text-ink-50 focus:border-gold outline-none text-sm transition-colors";
const photoSrc = (member) => member.photo_url
    ? (member.photo_url.startsWith("http") ? member.photo_url : `${process.env.REACT_APP_BACKEND_URL}${member.photo_url}`)
    : member.photo_file_id ? `${API}/profile/agency-team/photos/${member.photo_file_id}` : "";

const newMember = () => ({ id: `member_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`, first_name: "", last_name: "", role: "", email: "", photo_file_id: "" });

export const AgencyTeamEditor = ({ members, onChange, testPrefix = "cw" }) => {
    const [uploadingId, setUploadingId] = useState("");

    const update = (id, patch) => onChange(members.map((member) => member.id === id ? { ...member, ...patch } : member));
    const remove = (id) => onChange(members.filter((member) => member.id !== id));

    const upload = async (member, file) => {
        if (!file) return;
        setUploadingId(member.id);
        try {
            const formData = new FormData();
            formData.append("file", file);
            const { data } = await api.post("/profile/agency-team/photo", formData);
            update(member.id, { photo_file_id: data.file_id, photo_url: data.photo_url });
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Caricamento foto non riuscito");
        } finally {
            setUploadingId("");
        }
    };

    return (
        <div data-testid={`${testPrefix}-team-editor`} className="pt-5 mt-2 border-t border-white/5 space-y-5">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-gold">Team pubblico</div>
                    <p className="text-xs text-ink-400 mt-1">Aggiungi solo le informazioni che vuoi rendere pubbliche.</p>
                </div>
                <button data-testid={`${testPrefix}-team-add`} type="button" onClick={() => onChange([...members, newMember()])} className="cta-outline px-3 py-2 text-xs flex items-center gap-2 shrink-0">
                    <Plus size={13} /> Aggiungi
                </button>
            </div>

            {members.length === 0 && (
                <div data-testid={`${testPrefix}-team-empty`} className="border border-dashed border-white/15 p-6 text-center text-sm text-ink-500">Nessun membro inserito.</div>
            )}

            {members.map((member, index) => (
                <TeamMemberRow
                    key={member.id}
                    member={member}
                    index={index}
                    uploading={uploadingId === member.id}
                    testPrefix={testPrefix}
                    onUpdate={(patch) => update(member.id, patch)}
                    onUpload={(file) => upload(member, file)}
                    onRemove={() => remove(member.id)}
                />
            ))}
        </div>
    );
};

const TeamMemberRow = ({ member, index, uploading, onUpdate, onUpload, onRemove, testPrefix }) => {
    const fileRef = useRef(null);
    const src = photoSrc(member);
    return (
        <div data-testid={`${testPrefix}-team-member-${index}`} className="border border-white/10 bg-black/15 p-4 md:p-5">
            <div className="flex flex-col md:flex-row gap-5">
                <div className="flex md:flex-col items-center gap-3 md:w-28 shrink-0">
                    <div data-testid={`${testPrefix}-team-photo-preview-${index}`} className="w-20 h-20 rounded-full overflow-hidden border border-white/15 bg-ink-950 grid place-items-center">
                        {src ? <img src={src} alt="Anteprima membro" className="w-full h-full object-cover" /> : <UserRound size={28} className="text-ink-500" />}
                    </div>
                    <input ref={fileRef} data-testid={`${testPrefix}-team-photo-input-${index}`} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} />
                    <button data-testid={`${testPrefix}-team-photo-button-${index}`} type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="text-xs text-gold hover:text-gold-soft flex items-center gap-1.5 disabled:opacity-50">
                        <ImagePlus size={13} /> {uploading ? "Caricamento…" : src ? "Sostituisci" : "Carica foto"}
                    </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1 min-w-0">
                    <input data-testid={`${testPrefix}-team-first-name-${index}`} className={inputCls} placeholder="Nome (opzionale)" value={member.first_name || ""} onChange={(e) => onUpdate({ first_name: e.target.value })} />
                    <input data-testid={`${testPrefix}-team-last-name-${index}`} className={inputCls} placeholder="Cognome (opzionale)" value={member.last_name || ""} onChange={(e) => onUpdate({ last_name: e.target.value })} />
                    <input data-testid={`${testPrefix}-team-role-${index}`} className={inputCls} placeholder="Ruolo (opzionale)" value={member.role || ""} onChange={(e) => onUpdate({ role: e.target.value })} />
                    <label className="relative">
                        <Mail size={14} className="absolute left-3 top-3 text-ink-500" />
                        <input data-testid={`${testPrefix}-team-email-${index}`} type="email" className={`${inputCls} pl-9`} placeholder="Email (opzionale)" value={member.email || ""} onChange={(e) => onUpdate({ email: e.target.value })} />
                    </label>
                </div>
                <button data-testid={`${testPrefix}-team-remove-${index}`} type="button" onClick={onRemove} className="self-start p-2 text-red-300 hover:text-red-200 hover:bg-red-900/20" aria-label={`Rimuovi membro ${index + 1}`}>
                    <Trash2 size={16} />
                </button>
            </div>
        </div>
    );
};